import React, { useState } from 'react'
import toast from 'react-hot-toast'
import { useMutation } from 'react-query'
import { AxiosError } from 'axios'

import { Divider, Grid, Typography } from '@material-ui/core'
import { DropzoneArea } from 'material-ui-dropzone'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogTitle from '@material-ui/core/DialogTitle'

import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import Paper from '@material-ui/core/Paper'

import { skuit } from 'src/services/skuit'
import { CustomAxiosConfig } from 'src/services/SC/types'
import { Notification } from 'src/components/Notification'

const createData = (field: string, description: string, requirements: string) => {
  return { field, description, requirements }
}

const rows = [
  createData('facility', 'SCW Facility SquID', 'Required'),
  createData('tracking_number', 'Inbound Shipment Tracking Number', 'Required'),
  createData('carrier', 'SCAC code of the carrier that will bring the load', 'Not Required'),
  createData('expected_pallet_count', 'The number of pallets expected on this inbound shipment', 'Not Required'),
  createData('sku', 'SKU that is expected to arrive in this ASN', 'Not Required'),
  createData('num_units', 'Number of units of the specified SKU that are expected', 'Not Required'),
  createData(
    'is_transload',
    'Boolean (true/false) indicating if the ASN is a Transload',
    'Not required (default false)'
  ),
]

const ExpectedFormatTable = () => (
  <TableContainer component={Paper}>
    <Table aria-label="expected format">
      <TableHead>
        <TableRow>
          <TableCell>Field </TableCell>
          <TableCell align="left">Description</TableCell>
          <TableCell align="left">Requirements</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.field}>
            <TableCell component="th" scope="row">
              {row.field}
            </TableCell>
            <TableCell align="left">{row.description}</TableCell>
            <TableCell align="left">{row.requirements}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
)

interface BulkASNUploadDialogProps {
  open: boolean
  handleClose: () => void
  onSuccess?: () => void
  clientGuid: string
}

type ErrorResponse = {
  detail: string
}

export const BulkASNUploadDialog = (props: BulkASNUploadDialogProps) => {
  const { open, handleClose, onSuccess, clientGuid } = props

  const [selectedFile, setSelectedFile] = useState<File | undefined>()
  const [bulkErrors, setBulkErrors] = useState<any[]>([])
  const [detailError, setDetailError] = useState<ErrorResponse | null>(null)

  const { mutate: doUpload } = useMutation((config: CustomAxiosConfig) => skuit.sc.asn.uploadCsv(clientGuid, config), {
    onSuccess: () => {
      onSuccess?.()
      toast.success(() => <Notification message={`Successfully created ASN from csv`} />)

      handleClose()
    },
    onError: (error: AxiosError) => {
      if (error.response?.status === 500) {
        setBulkErrors([])
      } else {
        if (error.response?.data.detail) {
          setDetailError(error.response.data)
        } else {
          setBulkErrors(error.response?.data)
        }
      }

      toast.success(() => <Notification message={`Failed to process CSV: ${error}`} />)
    },
  })

  const uploadCsv = (file: File) => {
    if (file) {
      setBulkErrors([])
      const data = { file }
      doUpload({ data })
    }
  }

  return (
    <div>
      <Dialog
        fullWidth
        maxWidth="md"
        open={open}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title"> Bulk ASN Upload </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Grid container spacing={2}>
              <Grid item xs={12}>Expected Format</Grid>
              <Grid item>{ExpectedFormatTable()}</Grid>
              <Grid item container>
                {!!bulkErrors && !!bulkErrors.length && (
                  <Grid item xs={12}>
                    <Typography color="error">Errors in CSV:</Typography>
                    {bulkErrors.map((error: any, index: number) => (
                      <Typography key={index} color="error">
                        Line {error.line}:{' '}
                        {JSON.stringify(error.details)}
                      </Typography>
                    ))}
                  </Grid>
                )}
                {!!detailError && (
                  <Grid item xs={12}>
                    <Typography color="error">{detailError.detail}</Typography>
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid item>
              <Typography>
                To specify multiple SKUs expected on this inbound shipment, create additional rows with the
                ASN information duplicated. Note that any conflicting data (other than the Tracking Number)
                will be ignored. The first set of information for a Tracking Number will be used.
              </Typography>
            </Grid>
          </DialogContentText>
          <DropzoneArea
            dropzoneText="Drag and drop or click to add file"
            acceptedFiles={['.csv']}
            onChange={(loadedFiles) => {
              setSelectedFile(loadedFiles[0])
            }}
            filesLimit={1}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <div>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                if (selectedFile) {
                  uploadCsv(selectedFile)
                }
              }}
            >
              Create ASN
            </Button>
          </div>
          <Divider variant="middle" />
        </DialogActions>
      </Dialog>
    </div>
  )
}
